<template>
  <settings-page-card title="Backup & Restore">
    <div class="px-4 max-w-[900px] mb-[48px]">
      <p class="text-dark text-base text-[15px]">
        <b>
          How Backups Work
        </b>
        <br>
        ESTAGE automatically protects your work through two types of backups:
        <br>
        <br>
        <b>
          1. Page Backups (Short-Term)
        </b>
        <br>
        Every time you edit a page and click Save, or when your changes autosave, ESTAGE creates a short-term backup of that page.
        These restore points appear in the Page Backups section and are kept for 7 days before being automatically deleted.
        <br>
        <br>
        <b>
          2. Version Backups (Published Versions)
        </b>
        <br>
        When you publish a page and select “Add to Backup”, ESTAGE saves a permanent version of that page exactly as it was at the moment of publishing.
        These appear under Version Backups and remain stored until you publish a newer version with “Add to Backup” enabled - at which point the older version is replaced by the most recent one.
        <br>
        <br>
        <template v-if="projectOwner">
          <b>
            3. Master Backups (Full Project Snapshots)
          </b>
          <br>
          You can also manually create a full, project-wide backup at any time. These are stored in the Master Backup section below.
          <br>
          <br>
        </template>
        Use these tools to confidently restore any page or project to a previous state whenever needed.
      </p>
    </div>

    <b-tabs
      class="px-4"
      nav-class="border-0"
      active-nav-item-class="font-semibold text-black bg-transparent"
    >
      <b-tab
        title="Page Backups"
        title-link-class="text-sm text-[#878787] hover:text-black transition-colors !px-[20px] !py-[12px] !bg-[#F9FBFF] !border-t !border-[#DFE2E5]"
        active
      >
        <div v-if="backups.length === 0">
          <div class="text-center text-sm text-[#878787] p-4 border border-[#DFE2E5]">
            <b-spinner
              v-if="initLoading"
              variant="primary"
            />
            <span v-else>
              No backups found
            </span>
          </div>
        </div>
        <div
          v-for="(backup, dateKey) in backups"
          v-else
          :key="dateKey"
          class="w-full border border-[#DFE2E5]"
        >
          <button
            type="button"
            class="flex items-center justify-between w-full border-b border-[#DFE2E5] last:border-b-0 !bg-[#F9FBFF] hover:!bg-[#F1F4F9] px-[16px] py-[12px] text-start text-base font-semibold transition-colors"
            @click="toggleBackup(dateKey)"
          >
            {{ formatBackupDate(backup[0].created_at) }}

            <svg
              width="9"
              height="5"
              viewBox="0 0 9 5"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              :class="{'rotate-180': activeTab !== dateKey}"
            >
              <path
                d="M0.75 4.25L4.25 0.75L7.75 4.25"
                stroke="#525252"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <b-collapse
            :id="'backup-' + dateKey"
            :visible="activeTab === dateKey"
            accordion="backups-accordion"
            role="tabpanel"
            class="visible"
          >
            <b-card-body
              v-for="page in backup"
              :key="page.id"
              class="p-0"
            >
              <div class="flex flex-col">
                <div class="flex bg-[#F1F4F9] border-y border-[#DFE2E5]">
                  <div class="flex items-center w-[256px] px-[16px] py-[10px] text-sm text-[rgba(0,0,0,.4)]">
                    Date Created
                  </div>
                  <div class="flex items-center w-full flex-1 px-[16px] py-[10px] text-sm text-[rgba(0,0,0,.4)]">
                    Page
                  </div>
                  <div class="flex items-center w-[360px] px-[24px] py-[10px] text-sm text-[rgba(0,0,0,.4)]">
                    Actions
                  </div>
                </div>

                <div class="flex bg-white border-b border-[#DFE2E5]">
                  <div class="flex items-center gap-x-2 w-[256px] px-[16px] py-[12px] text-sm text-[rgba(0,0,0,.4)] border-r border-[#DFE2E5]">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 20 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M10.0003 4.99996V9.99996L13.3337 11.6666M18.3337 9.99996C18.3337 14.6023 14.6027 18.3333 10.0003 18.3333C5.39795 18.3333 1.66699 14.6023 1.66699 9.99996C1.66699 5.39759 5.39795 1.66663 10.0003 1.66663C14.6027 1.66663 18.3337 5.39759 18.3337 9.99996Z"
                        stroke="#7E7E7E"
                        stroke-width="1.3"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>

                    {{ formatBackupDate(page.created_at, true) }}
                  </div>
                  <div class="flex items-center w-full flex-1 px-[16px] py-[12px] text-base text-black font-semibold">
                    {{ page.title }}
                  </div>
                  <div class="flex items-center justify-end gap-x-2 w-[360px] px-[16px] py-[12px] text-sm text-[rgba(0,0,0,.4)]">
                    <b-btn
                      variant="transparent"
                      class="!bg-[#E6E6E6] hover:opacity-80 h-[40px] px-[16px] text-black !font-normal !text-sm"
                      @click="openPreview(page)"
                    >
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M1.81509 9.53488C1.71295 9.37315 1.66188 9.29229 1.63329 9.16756C1.61182 9.07387 1.61182 8.92613 1.63329 8.83244C1.66188 8.70771 1.71295 8.62685 1.81509 8.46512C2.65915 7.12863 5.17155 3.75 9.0003 3.75C12.8291 3.75 15.3415 7.12863 16.1855 8.46512C16.2877 8.62685 16.3387 8.70771 16.3673 8.83244C16.3888 8.92613 16.3888 9.07387 16.3673 9.16756C16.3387 9.29229 16.2877 9.37315 16.1855 9.53488C15.3415 10.8714 12.8291 14.25 9.0003 14.25C5.17155 14.25 2.65915 10.8714 1.81509 9.53488Z"
                          stroke="#3D3D3D"
                          stroke-width="1.3"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M9.0003 11.25C10.2429 11.25 11.2503 10.2426 11.2503 9C11.2503 7.75736 10.2429 6.75 9.0003 6.75C7.75766 6.75 6.7503 7.75736 6.7503 9C6.7503 10.2426 7.75766 11.25 9.0003 11.25Z"
                          stroke="#3D3D3D"
                          stroke-width="1.3"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>

                      Preview
                    </b-btn>

                    <b-btn
                      variant="transparent"
                      class="!bg-[#E6E6E6] hover:opacity-80 h-[40px] px-[16px] text-black !font-normal !text-sm"
                      @click="downloadBackup(page.id)"
                    >
                      <svg
                        width="19"
                        height="19"
                        viewBox="0 0 19 19"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M16.625 11.875V12.825C16.625 14.1551 16.625 14.8202 16.3661 15.3282C16.1384 15.7751 15.7751 16.1384 15.3282 16.3661C14.8202 16.625 14.1551 16.625 12.825 16.625H6.175C4.84488 16.625 4.17981 16.625 3.67177 16.3661C3.22489 16.1384 2.86156 15.7751 2.63386 15.3282C2.375 14.8202 2.375 14.1551 2.375 12.825V11.875M13.4583 7.91667L9.5 11.875M9.5 11.875L5.54167 7.91667M9.5 11.875V2.375"
                          stroke="#3D3D3D"
                          stroke-width="1.3"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>
                    </b-btn>

                    <delete-confirm
                      v-if="canCreatePages"
                      title="Are you sure you want to restore this backup?"
                      description="Just in case, we will save the current version of your project when you restore to this version"
                      confirm-variant="primary"
                      yes-btn-text="Yes, Restore"
                      no-btn-text="Cancel"
                      @confirm="restorePage({id: page.id, hash: page.hash})"
                    >
                      <template #activator="{on}">
                        <b-btn
                          variant="transparent"
                          class="!bg-[#4F83E3] hover:opacity-80 h-[40px] px-[16px] text-white !font-normal !text-sm"
                          :class="{'opacity-60': loading}"
                          :disabled="loading"
                          v-on="on"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13.9997 6.66667C13.9997 6.66667 12.663 4.84548 11.5771 3.75883C10.4912 2.67218 8.99061 2 7.33301 2C4.0193 2 1.33301 4.68629 1.33301 8C1.33301 11.3137 4.0193 14 7.33301 14C10.0684 14 12.3763 12.1695 13.0985 9.66667M13.9997 6.66667V2.66667M13.9997 6.66667H9.99967"
                              stroke="white"
                              stroke-width="1.3"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                            />
                          </svg>

                          Restore
                        </b-btn>
                      </template>
                    </delete-confirm>
                  </div>
                </div>
              </div>
            </b-card-body>
          </b-collapse>
        </div>
      </b-tab>
      <b-tab
        title="Version Backups"
        title-link-class="text-sm text-[#878787] hover:text-black transition-colors !px-[20px] !py-[12px] !bg-[#F9FBFF] !border-t !border-[#DFE2E5]"
      >
        <div v-if="versionBackups.length === 0">
          <div class="text-center text-sm text-[#878787] p-4 border border-[#DFE2E5]">
            <b-spinner
              v-if="initLoading"
              variant="primary"
            />
            <span v-else>
              No backups found
            </span>
          </div>
        </div>
        <div
          v-for="(backup, dateKey) in versionBackups"
          v-else
          :key="`version-${dateKey}`"
          class="w-full border border-[#DFE2E5]"
        >
          <div class="flex flex-col">
            <div class="flex bg-[#F1F4F9] border-y border-[#DFE2E5]">
              <div class="flex items-center w-[256px] px-[16px] py-[10px] text-sm text-[rgba(0,0,0,.4)]">
                Date Created
              </div>
              <div class="flex items-center w-full flex-1 px-[16px] py-[10px] text-sm text-[rgba(0,0,0,.4)]">
                Page
              </div>
              <div class="flex items-center w-[360px] px-[24px] py-[10px] text-sm text-[rgba(0,0,0,.4)]">
                Actions
              </div>
            </div>

            <div class="flex bg-white">
              <div class="flex items-center gap-x-2 w-[256px] px-[16px] py-[12px] text-sm text-[rgba(0,0,0,.4)] border-r border-[#DFE2E5]">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.0003 4.99996V9.99996L13.3337 11.6666M18.3337 9.99996C18.3337 14.6023 14.6027 18.3333 10.0003 18.3333C5.39795 18.3333 1.66699 14.6023 1.66699 9.99996C1.66699 5.39759 5.39795 1.66663 10.0003 1.66663C14.6027 1.66663 18.3337 5.39759 18.3337 9.99996Z"
                    stroke="#7E7E7E"
                    stroke-width="1.3"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>

                {{ formatBackupDate(backup.created_at, true) }}
              </div>
              <div class="flex items-center w-full flex-1 px-[16px] py-[12px] text-base text-black font-semibold">
                {{ backup.title }}
              </div>
              <div class="flex items-center justify-end gap-x-2 w-[360px] px-[16px] py-[12px] text-sm text-[rgba(0,0,0,.4)]">
                <b-btn
                  variant="transparent"
                  class="!bg-[#E6E6E6] hover:opacity-80 h-[40px] px-[16px] text-black !font-normal !text-sm"
                  @click="openPreview(backup)"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M1.81509 9.53488C1.71295 9.37315 1.66188 9.29229 1.63329 9.16756C1.61182 9.07387 1.61182 8.92613 1.63329 8.83244C1.66188 8.70771 1.71295 8.62685 1.81509 8.46512C2.65915 7.12863 5.17155 3.75 9.0003 3.75C12.8291 3.75 15.3415 7.12863 16.1855 8.46512C16.2877 8.62685 16.3387 8.70771 16.3673 8.83244C16.3888 8.92613 16.3888 9.07387 16.3673 9.16756C16.3387 9.29229 16.2877 9.37315 16.1855 9.53488C15.3415 10.8714 12.8291 14.25 9.0003 14.25C5.17155 14.25 2.65915 10.8714 1.81509 9.53488Z"
                      stroke="#3D3D3D"
                      stroke-width="1.3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M9.0003 11.25C10.2429 11.25 11.2503 10.2426 11.2503 9C11.2503 7.75736 10.2429 6.75 9.0003 6.75C7.75766 6.75 6.7503 7.75736 6.7503 9C6.7503 10.2426 7.75766 11.25 9.0003 11.25Z"
                      stroke="#3D3D3D"
                      stroke-width="1.3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  Preview
                </b-btn>

                <b-btn
                  variant="transparent"
                  class="!bg-[#E6E6E6] hover:opacity-80 h-[40px] px-[16px] text-black !font-normal !text-sm"
                  @click="downloadBackup(backup.id)"
                >
                  <svg
                    width="19"
                    height="19"
                    viewBox="0 0 19 19"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M16.625 11.875V12.825C16.625 14.1551 16.625 14.8202 16.3661 15.3282C16.1384 15.7751 15.7751 16.1384 15.3282 16.3661C14.8202 16.625 14.1551 16.625 12.825 16.625H6.175C4.84488 16.625 4.17981 16.625 3.67177 16.3661C3.22489 16.1384 2.86156 15.7751 2.63386 15.3282C2.375 14.8202 2.375 14.1551 2.375 12.825V11.875M13.4583 7.91667L9.5 11.875M9.5 11.875L5.54167 7.91667M9.5 11.875V2.375"
                      stroke="#3D3D3D"
                      stroke-width="1.3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </b-btn>

                <delete-confirm
                  v-if="canCreatePages"
                  title="Are you sure you want to restore this backup?"
                  description="Just in case, we will save the current version of your project when you restore to this version"
                  confirm-variant="primary"
                  yes-btn-text="Yes, Restore"
                  no-btn-text="Cancel"
                  @confirm="restorePage({id: backup.id, hash: backup.hash})"
                >
                  <template #activator="{on}">
                    <b-btn
                      variant="transparent"
                      class="!bg-[#4F83E3] hover:opacity-80 h-[40px] px-[16px] text-white !font-normal !text-sm"
                      :class="{'opacity-60': loading}"
                      :disabled="loading"
                      v-on="on"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M13.9997 6.66667C13.9997 6.66667 12.663 4.84548 11.5771 3.75883C10.4912 2.67218 8.99061 2 7.33301 2C4.0193 2 1.33301 4.68629 1.33301 8C1.33301 11.3137 4.0193 14 7.33301 14C10.0684 14 12.3763 12.1695 13.0985 9.66667M13.9997 6.66667V2.66667M13.9997 6.66667H9.99967"
                          stroke="white"
                          stroke-width="1.3"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>

                      Restore
                    </b-btn>
                  </template>
                </delete-confirm>
              </div>
            </div>
          </div>
        </div>
      </b-tab>
      <b-tab
        v-if="projectOwner"
        title="Master Backups"
        title-link-class="text-sm text-[#878787] hover:text-black transition-colors !px-[20px] !py-[12px] !bg-[#F9FBFF] !border-t !border-[#DFE2E5]"
      >
        <div class="w-full border border-[#DFE2E5]">
          <div class="flex items-center px-[16px] py-[16px] gap-x-4">
            <b-btn
              variant="transparent"
              class="!bg-[#4F83E3] hover:opacity-80 h-[40px] px-[16px] text-white !font-normal !text-sm"
              :class="{'opacity-60': loading || !isAvailable}"
              :disabled="loading || !isAvailable"
              @click="postMasterBackups"
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                class="mr-1"
              >
                <path
                  d="M2.7819 5.83329C4.22277 3.34245 6.91587 1.66663 10.0004 1.66663C14.6028 1.66663 18.3337 5.39759 18.3337 9.99996C18.3337 14.6023 14.6028 18.3333 10.0004 18.3333C6.91587 18.3333 4.22277 16.6575 2.7819 14.1666M10.0003 13.3333L13.3337 9.99996M13.3337 9.99996L10.0003 6.66663M13.3337 9.99996H1.66699"
                  stroke="white"
                  stroke-width="1.3"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>

              Create Master Backup
            </b-btn>

            <span
              v-if="!loading && !isAvailable"
              class="text-base"
            >
              Next Master Backup Available in <b>{{ nextMasterBackupAt(lastBackupAt) }}</b>
            </span>
          </div>
          <div
            v-for="(backup, dateKey) in masterBackups"
            :key="`version-${dateKey}`"
          >
            <div class="flex flex-col">
              <div class="flex bg-[#F1F4F9] border-y border-[#DFE2E5]">
                <div class="flex items-center w-[256px] px-[16px] py-[10px] text-sm text-[rgba(0,0,0,.4)]">
                  Date Created
                </div>
                <div class="flex items-center w-full flex-1 px-[16px] py-[10px] text-sm text-[rgba(0,0,0,.4)]">
                  Project
                </div>
                <div class="flex items-center w-[360px] px-[24px] py-[10px] text-sm text-[rgba(0,0,0,.4)]">
                  Actions
                </div>
              </div>

              <div class="flex bg-white">
                <div class="flex items-center gap-x-2 w-[256px] px-[16px] py-[12px] text-sm text-[rgba(0,0,0,.4)] border-r border-[#DFE2E5]">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.0003 4.99996V9.99996L13.3337 11.6666M18.3337 9.99996C18.3337 14.6023 14.6027 18.3333 10.0003 18.3333C5.39795 18.3333 1.66699 14.6023 1.66699 9.99996C1.66699 5.39759 5.39795 1.66663 10.0003 1.66663C14.6027 1.66663 18.3337 5.39759 18.3337 9.99996Z"
                      stroke="#7E7E7E"
                      stroke-width="1.3"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>

                  {{ formatBackupDate(backup.created_at, true) }}
                </div>
                <div class="flex items-center w-full flex-1 px-[16px] py-[12px] text-base text-black font-semibold">
                  {{ project.name }}
                </div>
                <div class="flex items-center justify-end gap-x-2 w-[360px] px-[16px] py-[12px] text-sm text-[rgba(0,0,0,.4)]">
                  <!--                  <b-btn-->
                  <!--                    variant="transparent"-->
                  <!--                    class="!bg-[#E6E6E6] hover:opacity-80 h-[40px] px-[16px] text-black !font-normal !text-sm"-->
                  <!--                    @click="downloadMasterBackup(backup.backup_file)"-->
                  <!--                  >-->
                  <!--                    <svg-->
                  <!--                      width="19"-->
                  <!--                      height="19"-->
                  <!--                      viewBox="0 0 19 19"-->
                  <!--                      fill="none"-->
                  <!--                      xmlns="http://www.w3.org/2000/svg"-->
                  <!--                    >-->
                  <!--                      <path-->
                  <!--                        d="M16.625 11.875V12.825C16.625 14.1551 16.625 14.8202 16.3661 15.3282C16.1384 15.7751 15.7751 16.1384 15.3282 16.3661C14.8202 16.625 14.1551 16.625 12.825 16.625H6.175C4.84488 16.625 4.17981 16.625 3.67177 16.3661C3.22489 16.1384 2.86156 15.7751 2.63386 15.3282C2.375 14.8202 2.375 14.1551 2.375 12.825V11.875M13.4583 7.91667L9.5 11.875M9.5 11.875L5.54167 7.91667M9.5 11.875V2.375"-->
                  <!--                        stroke="#3D3D3D"-->
                  <!--                        stroke-width="1.3"-->
                  <!--                        stroke-linecap="round"-->
                  <!--                        stroke-linejoin="round"-->
                  <!--                      />-->
                  <!--                    </svg>-->
                  <!--                  </b-btn>-->

                  <delete-confirm
                    title="Are you sure you want to restore this backup?"
                    description="This backup will be restored as a new project in your account."
                    confirm-variant="primary"
                    yes-btn-text="Yes, Restore"
                    no-btn-text="Cancel"
                    @confirm="backupProject(backup.id)"
                  >
                    <template #activator="{on}">
                      <b-btn
                        variant="transparent"
                        class="!bg-[#4F83E3] hover:opacity-80 h-[40px] px-[16px] text-white !font-normal !text-sm"
                        :class="{'opacity-60': loading}"
                        :disabled="loading"
                        v-on="on"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 16 16"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M13.9997 6.66667C13.9997 6.66667 12.663 4.84548 11.5771 3.75883C10.4912 2.67218 8.99061 2 7.33301 2C4.0193 2 1.33301 4.68629 1.33301 8C1.33301 11.3137 4.0193 14 7.33301 14C10.0684 14 12.3763 12.1695 13.0985 9.66667M13.9997 6.66667V2.66667M13.9997 6.66667H9.99967"
                            stroke="white"
                            stroke-width="1.3"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>

                        Restore
                      </b-btn>
                    </template>
                  </delete-confirm>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-tab>
    </b-tabs>
  </settings-page-card>
</template>

<script>
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import {mapActions, mapGetters, mapState} from 'vuex'
import SettingsPageCard from '@/layouts/dashboard/SettingsPageCard.vue'
import DeleteConfirm from '@/components/editor/modals/DeleteConfirm.vue'

dayjs.extend(utc)
dayjs.extend(timezone)

export default {
  name: 'Backup',
  components: {
    DeleteConfirm,
    SettingsPageCard
  },
  data () {
    return {
      initLoading: false,
      activeTab: null
    }
  },
  computed: {
    ...mapState('projects', {
      project: state => state.project,
      projectOwner: state => state.projectOwner
    }),
    ...mapState('backups', {
      loading: state => state.loading,
      backups: state => state.backups,
      versionBackups: state => state.versionBackups,
      masterBackups: state => state.masterBackups,
      isAvailable: state => state.isAvailable,
      lastBackupAt: state => state.lastBackupAt
    }),
    ...mapGetters('projects', {
      checkPermissions: 'checkPermissions'
    }),
    canCreatePages () {
      return this.checkPermissions('pages.create')
    }
  },
  methods: {
    ...mapActions('backups', {
      getBackupsList: 'getBackupsList',
      getVersionBackupsList: 'getVersionBackupsList',
      getMasterBackups: 'getMasterBackups',
      checkIsAvailable: 'checkIsAvailable',
      downloadBackup: 'downloadBackup',
      downloadMasterBackup: 'downloadMasterBackup',
      postMasterBackups: 'postMasterBackups',
      restorePage: 'restorePage',
      restoreMasterBackups: 'restoreMasterBackups'
    }),
    toggleBackup (id) {
      this.activeTab = this.activeTab === id ? null : id
    },
    formatBackupDate (isoString, withTime = false) {
      const tz = dayjs.tz.guess()
      const m = dayjs.utc(isoString).tz(tz)

      const formatted = m.format(withTime ? 'MM/DD/YYYY HH:mm' : 'MM/DD/YYYY')

      const now = dayjs().tz(tz)

      if (!withTime && m.isSame(now, 'day')) {
        return `${formatted} (Today)`
      }

      return formatted
    },
    openPreview (page) {
      window.open(`https://${this.project.subdomain}.estage.site/backup/${page.id}/${page.hash}`, '_blank')
    },
    async backupProject (id) {
      await this.restoreMasterBackups(id)

      await this.$swal.fire({
        icon: 'success',
        title: 'Project is Restoring',
        text: 'This make take up to 1-5 minutes. You may close this message.',
        showConfirmButton: true,
        confirmButtonText: 'Got It'
      })
    },
    nextMasterBackupAt () {
      if (!this.lastBackupAt) return null

      const normalizedIso = String(this.lastBackupAt).replace(/\.(\d{3})\d+Z$/, '.$1Z')

      const last = dayjs.utc(normalizedIso)
      const next = last.add(24, 'hour')
      const now = dayjs.utc()

      const diffMs = next.diff(now)

      if (diffMs <= 0) {
        return {
          ready: true,
          nextAt: next.toISOString(),
          hours: 0,
          minutes: 0,
          text: '0h'
        }
      }

      const totalMinutes = Math.ceil(diffMs / 60000)
      const hours = Math.floor(totalMinutes / 60)

      return `${hours}h`
    }
  },
  async mounted () {
    this.initLoading = true
    await this.$store.dispatch('projects/hasAccessToPage', 'project_settings.backup')
    await this.getBackupsList()
    await this.getVersionBackupsList()
    await this.getMasterBackups()
    await this.checkIsAvailable()
    this.initLoading = false
  }
}
</script>